import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Divider,
  Stack,
} from "@mui/material";
import { MonetizationOn as MonetizationOnIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Input from "../../components/Input";
import { useSnackbar } from "../../context/SnackbarContext";
import { societyService } from "../../services/society/societyService";
import StoreIcon from "@mui/icons-material/Store";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";

interface SocietyFormData {
  name: string;
  order: string;
  minimumOrder: string;
}

const SocietyCreatePage = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { showSnackbar } = useSnackbar();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SocietyFormData>({
    defaultValues: {
      name: "",
      order: "",
      minimumOrder: "",
    },
  });

  const onSubmit = async (data: SocietyFormData) => {
    try {
      setIsLoading(true);
      await societyService.createSociety({ ...data });
      navigate("/societies");
      showSnackbar("Sociedad creada correctamente", "success", 3000);
    } catch (error: any) {
      showSnackbar(
        error.response?.data?.message || "Error al crear sociedad",
        "error",
        4000
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/societies");
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Form Card */}
      <Card elevation={2}>
        <CardContent sx={{ p: 4 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 3 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={() => navigate("/home")}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Inicio
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={() => navigate("/societies")}
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Sociedades
            </Link>
            <Typography
              color="text.primary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              Crear Sociedad
            </Typography>
          </Breadcrumbs>

          {/* Page Header */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Crear Nueva Sociedad
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Complete la información de la nueva sociedad
            </Typography>
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "flex",
                gap: 4,
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              {/* Form Fields */}
              <Box sx={{ flex: 1 }}>
                <Stack spacing={3}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    {/* Username */}
                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="name"
                        control={control}
                        rules={{
                          required: "El nombre es requerido",
                          minLength: {
                            value: 3,
                            message: "Mínimo 3 caracteres",
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            label="Nombre"
                            name="name"
                            field={field}
                            required
                            error={errors.name?.message}
                            icon={<StoreIcon />}
                            sx={{ width: "100%" }}
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Controller
                        name="order"
                        control={control}
                        rules={{
                          required: "El número de orden es requerido",
                          pattern: {
                            value: /^[0-9]+$/,
                            message: "Solo se permiten números",
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            label="Número de orden"
                            name="order"
                            field={field}
                            required
                            error={errors.order?.message}
                            icon={<ConfirmationNumberIcon />}
                            sx={{ width: "100%" }}
                          />
                        )}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ width: "50%" }}>
                    <Controller
                      name="minimumOrder"
                      control={control}
                      rules={{
                        required: "El mínimo de pedido es requerido",
                        pattern: {
                          value: /^[0-9]+$/,
                          message: "Solo se permiten números",
                        },
                        validate: (value) => {
                          const numValue = parseInt(value);
                          return (
                            numValue > 0 || "El valor debe ser mayor a cero"
                          );
                        },
                      }}
                      render={({ field }) => (
                        <Input
                          label="Mínimo de pedido"
                          name="minimumOrder"
                          field={field}
                          required
                          error={errors.minimumOrder?.message}
                          icon={<MonetizationOnIcon />}
                          sx={{ width: "50%" }}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={handleCancel}
                sx={{ minWidth: 120 }}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{ minWidth: 120 }}
                disabled={isLoading}
                loading={isLoading}
              >
                Crear Sociedad
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SocietyCreatePage;
